import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Plus, Trash2, Clock, Film, Search, X, GripVertical, Pencil, ChevronLeft, MoreVertical, Calendar, Play } from 'lucide-react';
import VideoPreviewModal from '../components/VideoPreviewModal';
import { useAuth } from '../context/AuthContext';
import devLog from '../utils/devLog';

// Format swap time for display (converts 24hr to 12hr)
const formatSwapTime = (timeStr) => {
  if (!timeStr) return '12:00 AM';
  
  const [hours, minutes] = timeStr.split(':');
  let hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  
  if (hour === 0) hour = 12;
  else if (hour > 12) hour = hour - 12;
  
  return `${hour}:${minutes || '00'} ${period}`;
};

function Playlists() {
  const [playlists, setPlaylists] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistContentType, setNewPlaylistContentType] = useState(''); // 'interval' | 'exercise' — set at creation, gates layout assignment
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState('all');
  const [assetScope, setAssetScope] = useState('library');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const { currentUser, isAdmin } = useAuth();
  
  // Equipment profile state for filtering
  const [userEquipmentProfile, setUserEquipmentProfile] = useState({});
  const [equipmentList, setEquipmentList] = useState([]);
  
  // Mobile view state: 'list' or 'detail'
  const [mobileView, setMobileView] = useState('list');
  
  // 3-dot menu state for mobile
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  
  // Rename state
  const [editingPlaylistId, setEditingPlaylistId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const renameInputRef = useRef(null);

  // Tomorrow mode state
  const [viewMode, setViewMode] = useState('current'); // 'current' or 'tomorrow'

  // NEW: Video preview modal state
  const [previewAsset, setPreviewAsset] = useState(null);

  // Hardcoded account ID - will be dynamic with auth
  const accountId = 'hit10fitness';

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const playlistsQuery = isAdmin 
  ? collection(db, 'playlists')
  : query(collection(db, 'playlists'), where('userId', '==', currentUser?.uid));

const [playlistsSnapshot, assetsSnapshot, equipmentSnapshot, profileDoc] = await Promise.all([
  getDocs(playlistsQuery),
          getDocs(collection(db, 'assets')),
          getDocs(collection(db, 'equipment')),
          getDoc(doc(db, 'equipmentProfiles', isAdmin ? 'hit10fitness' : currentUser.uid))
        ]);

        // Admin view: show all playlists (both admin-owned and tenant-owned) for support/debugging.
        // User view: show only playlists belonging to current user.
        const playlistsData = playlistsSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })).filter(playlist => {
  if (isAdmin) return !playlist.userId || playlist.userId === currentUser?.uid;
return playlist.userId === currentUser?.uid;
});


        const assetsData = assetsSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })).filter(asset => asset.type !== 'layout');

        const equipment = equipmentSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        setEquipmentList(equipment);

        if (profileDoc.exists()) {
          const rawEquipment = profileDoc.data().equipment || {};
          const cleanedEquipment = {};
          for (const [key, value] of Object.entries(rawEquipment)) {
            if (key && key !== 'undefined' && key !== 'null' && /^[a-zA-Z0-9]{20}$/.test(key)) {
              cleanedEquipment[key] = value;
            }
          }
          setUserEquipmentProfile(cleanedEquipment);
        }

        playlistsData.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

        setPlaylists(playlistsData);
        setAssets(assetsData);

        if (playlistsData.length > 0) {
          setSelectedPlaylist(playlistsData[0]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [accountId, isAdmin, currentUser?.uid]);

  // Focus the rename input when editing starts
  useEffect(() => {
    if (editingPlaylistId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingPlaylistId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      const playlistsQuery = isAdmin 
  ? collection(db, 'playlists')
  : query(collection(db, 'playlists'), where('userId', '==', currentUser?.uid));

const [playlistsSnapshot, assetsSnapshot, equipmentSnapshot, profileDoc] = await Promise.all([
  getDocs(playlistsQuery),
        getDocs(collection(db, 'assets')),
        getDocs(collection(db, 'equipment')),
        getDoc(doc(db, 'equipmentProfiles', isAdmin ? 'hit10fitness' : currentUser.uid))
      ]);

      // Admin view: show all playlists (both admin-owned and tenant-owned) for support/debugging.
      // User view: show only playlists belonging to current user.
      const playlistsData = playlistsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(playlist => {
  if (isAdmin) return !playlist.userId || playlist.userId === currentUser?.uid;
return playlist.userId === currentUser?.uid;
});

devLog('Loaded playlists:', playlistsData.map(p => ({ 
  name: p.name, 
  pendingAssets: p.pendingAssets 
})));

      const assetsData = assetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(asset => asset.type !== 'layout');

      // Load equipment list
      const equipment = equipmentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEquipmentList(equipment);

      // Load equipment profile
      if (profileDoc.exists()) {
        const rawEquipment = profileDoc.data().equipment || {};
        // Clean up invalid keys
        const cleanedEquipment = {};
        for (const [key, value] of Object.entries(rawEquipment)) {
          if (key && key !== 'undefined' && key !== 'null' && /^[a-zA-Z0-9]{20}$/.test(key)) {
            cleanedEquipment[key] = value;
          }
        }
        setUserEquipmentProfile(cleanedEquipment);
      }

      playlistsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      setPlaylists(playlistsData);
      setAssets(assetsData);

      if (selectedPlaylist) {
        const updated = playlistsData.find(p => p.id === selectedPlaylist.id);
        if (updated) {
          setSelectedPlaylist(updated);
        }
      } else if (playlistsData.length > 0) {
        setSelectedPlaylist(playlistsData[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get equipment names that user has selected in their profile
  const getUserEquipmentNames = () => {
    const selectedEquipmentIds = Object.keys(userEquipmentProfile);
    return equipmentList
      .filter(eq => selectedEquipmentIds.includes(eq.id))
      .map(eq => eq.name);
  };

  // Check if a video matches the user's equipment profile
  const videoMatchesEquipmentProfile = (asset) => {
    if (asset.type !== 'video') return false; // Only show videos
    
    const videoEquipmentTags = asset.equipmentTags || [];
    if (videoEquipmentTags.length === 0) return true; // Videos with no tags always show
    
    const userEquipmentNames = getUserEquipmentNames();
    
    // ALL equipment tags on the video must be in user's profile
    return videoEquipmentTags.every(tag => userEquipmentNames.includes(tag));
  };

  // Filter assets for available assets section - only videos matching equipment profile
  const getFilteredAvailableAssets = () => {
    const playlistType = selectedPlaylist?.playlistContentType;
    return assets.filter(asset => {
      if (!videoMatchesEquipmentProfile(asset)) return false;
      // Gate (doorway 1): only content whose type matches the open playlist's type.
      // Derived the same way the existing filters do (exercise = exercise-builder source).
      if (playlistType) {
        const isExercise = asset.source === 'exercise-builder' && asset.pageMode !== 'program';
        const isInterval = asset.type === 'video' && asset.source !== 'exercise-builder';
        if (playlistType === 'exercise' && !isExercise) return false;
        if (playlistType === 'interval' && !isInterval) return false;
      }
      return true;
    });
  };

  // Exercise playlists can only use My files (the shared library is interval-only),
  // so force scope to 'myfiles' when an exercise playlist is open.
  useEffect(() => {
    if (selectedPlaylist?.playlistContentType === 'exercise') {
      setAssetScope('myfiles');
    }
  }, [selectedPlaylist]);

  const handleAddPlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    if (!newPlaylistContentType) {
      alert('Please choose a content type (Interval or Exercise) for this playlist');
      return;
    }

    try {
      const newPlaylist = {
        name: newPlaylistName,
        playlistContentType: newPlaylistContentType,
        userId: currentUser.uid,
        startTime: '06:00',
        endTime: '19:30',
        timezone: 'America/New_York',
        assets: [],
        pendingAssets: [],
        swapTime: null,
        order: playlists.length
      };

      await addDoc(collection(db, 'playlists'), newPlaylist);
      setNewPlaylistName('');
      setNewPlaylistContentType('');
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error('Error adding playlist:', error);
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    setOpenMenuId(null);
    if (window.confirm('Are you sure you want to delete this playlist?')) {
      try {
        await deleteDoc(doc(db, 'playlists', playlistId));
        if (selectedPlaylist?.id === playlistId) {
          setSelectedPlaylist(null);
          setMobileView('list');
          setViewMode('current');
        }
        fetchData();
      } catch (error) {
        console.error('Error deleting playlist:', error);
      }
    }
  };

  const handleSelectPlaylist = (playlist) => {
    if (editingPlaylistId !== playlist.id) {
      setSelectedPlaylist(playlist);
      setMobileView('detail');
      setOpenMenuId(null);
      setViewMode('current');
    }
  };

  const handleBackToList = () => {
    setMobileView('list');
    setViewMode('current');
  };

  const toggleMenu = (playlistId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === playlistId ? null : playlistId);
  };

  const startRenaming = (e, playlist) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setEditingPlaylistId(playlist.id);
    setEditingName(playlist.name);
  };

  const handleRenamePlaylist = async () => {
    if (!editingName.trim() || !editingPlaylistId) {
      cancelRenaming();
      return;
    }

    try {
      await updateDoc(doc(db, 'playlists', editingPlaylistId), {
        name: editingName.trim()
      });
      
      setPlaylists(prev => prev.map(p => 
        p.id === editingPlaylistId ? { ...p, name: editingName.trim() } : p
      ));
      
      if (selectedPlaylist?.id === editingPlaylistId) {
        setSelectedPlaylist(prev => ({ ...prev, name: editingName.trim() }));
      }
      
      setEditingPlaylistId(null);
      setEditingName('');
    } catch (error) {
      console.error('Error renaming playlist:', error);
    }
  };

  const cancelRenaming = () => {
    setEditingPlaylistId(null);
    setEditingName('');
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRenamePlaylist();
    } else if (e.key === 'Escape') {
      cancelRenaming();
    }
  };

  const handleAddAssetToPlaylist = async (assetId) => {
  if (!selectedPlaylist) return;

  try {
    if (viewMode === 'tomorrow') {
      const updatedPendingAssets = [...(selectedPlaylist.pendingAssets || []), assetId];
      
      // Auto-calculate swapAfter from playlist's swapTime
      const swapTime = selectedPlaylist.swapTime || '00:00';
      const [swapHour, swapMinute] = swapTime.split(':').map(Number);
      const now = new Date();
      const swapDate = new Date();
      swapDate.setHours(swapHour, swapMinute, 0, 0);
      
      // If this time has already passed today, set it for tomorrow
      if (swapDate <= now) {
        swapDate.setDate(swapDate.getDate() + 1);
      }
      
      const swapAfter = swapDate.getTime();
      
     const updateData = {
  pendingAssets: updatedPendingAssets,
  swapAfter: swapAfter
};

if (!selectedPlaylist.swapTime) {
  updateData.swapTime = swapTime;
}

await updateDoc(doc(db, 'playlists', selectedPlaylist.id), updateData);

      setSelectedPlaylist({...selectedPlaylist, pendingAssets: updatedPendingAssets, swapAfter: swapAfter});
    } else {
      const updatedAssets = [...(selectedPlaylist.assets || []), assetId];
      await updateDoc(doc(db, 'playlists', selectedPlaylist.id), {
        assets: updatedAssets
      });
      setSelectedPlaylist({...selectedPlaylist, assets: updatedAssets});
    }
    
    setShowAssetPicker(false);
    fetchData();
  } catch (error) {
    console.error('Error adding asset to playlist:', error);
  }
};

  const handleRemoveAssetFromPlaylist = async (assetId) => {
    if (!selectedPlaylist) return;

    try {
      if (viewMode === 'tomorrow') {
  const updatedPendingAssets = (selectedPlaylist.pendingAssets || []).filter(id => id !== assetId);
  
  await updateDoc(doc(db, 'playlists', selectedPlaylist.id), {
    pendingAssets: updatedPendingAssets
  });
  setSelectedPlaylist({
    ...selectedPlaylist, 
    pendingAssets: updatedPendingAssets
  });
} else {
        const updatedAssets = selectedPlaylist.assets.filter(id => id !== assetId);
        await updateDoc(doc(db, 'playlists', selectedPlaylist.id), {
          assets: updatedAssets
        });
        setSelectedPlaylist({...selectedPlaylist, assets: updatedAssets});
      }
      
      fetchData();
    } catch (error) {
      console.error('Error removing asset from playlist:', error);
    }
  };

  const getCurrentAssets = () => {
    if (!selectedPlaylist) return [];
    if (viewMode === 'tomorrow') {
      return selectedPlaylist.pendingAssets || [];
    }
    return selectedPlaylist.assets || [];
  };

  const hasPendingContent = (playlist) => {
    return playlist.pendingAssets && playlist.pendingAssets.length > 0;
  };

  const getAssetById = (assetId) => {
    return assets.find(a => a.id === assetId);
  };

  const formatDuration = (ms) => {
    if (!ms) return '0.0 sec';
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds} sec`;
  };

  const getTotalDuration = (playlist) => {
    if (!playlist.assets || playlist.assets.length === 0) return '0.0 sec';
    const totalMs = playlist.assets.reduce((total, assetId) => {
      const asset = getAssetById(assetId);
      return total + (asset?.durationMs || 0);
    }, 0);
    return formatDuration(totalMs);
  };

  // Drag and Drop handlers for playlists
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newPlaylists = [...playlists];
    const [draggedItem] = newPlaylists.splice(draggedIndex, 1);
    newPlaylists.splice(dropIndex, 0, draggedItem);

    setPlaylists(newPlaylists);
    setDraggedIndex(null);
    setDragOverIndex(null);

    try {
      const updatePromises = newPlaylists.map((playlist, index) => 
        updateDoc(doc(db, 'playlists', playlist.id), { order: index })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error saving playlist order:', error);
      fetchData();
    }
  };

  if (loading) {
    return <div className="loading">Loading playlists...</div>;
  }

  const displayAssets = getCurrentAssets();
  const filteredAvailableAssets = getFilteredAvailableAssets();

  return (
    <div>
      {/* Desktop Header */}
      <div className="page-header desktop-only">
        <h1 className="page-title" style={{ fontSize: '20px', fontWeight: 500 }}>Playlists</h1>
      </div>

      {/* DESKTOP LAYOUT */}
      <div className="sidebar-layout desktop-only">
        {/* Sidebar - Playlist List */}
        <div className="sidebar">
          <div className="card">
            <button 
              className="btn btn-primary" 
              style={{ width: '100%', marginBottom: '16px' }}
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={18} />
              Create Playlist
            </button>

            <div>
              {playlists.map((playlist, index) => (
                <div
                  key={playlist.id}
                  draggable={editingPlaylistId !== playlist.id}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`playlist-item ${selectedPlaylist?.id === playlist.id && viewMode === 'current' ? 'active' : ''}`}
                  onClick={() => handleSelectPlaylist(playlist)}
                  style={{
                    cursor: editingPlaylistId === playlist.id ? 'default' : 'grab',
                    borderTop: dragOverIndex === index && draggedIndex > index ? '2px solid #d32f2f' : 'none',
                    borderBottom: dragOverIndex === index && draggedIndex < index ? '2px solid #d32f2f' : 'none',
                    opacity: draggedIndex === index ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <GripVertical size={14} color="#999" style={{ flexShrink: 0 }} />
                  <div
                    className="playlist-dot"
                    style={hasPendingContent(playlist) ? { backgroundColor: '#10b981' } : undefined}
                    title={hasPendingContent(playlist) ? 'Has tomorrow content' : undefined}
                  ></div>
                  
                  {editingPlaylistId === playlist.id ? (
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      onBlur={handleRenamePlaylist}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        flex: 1,
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d32f2f',
                        borderRadius: '4px',
                        outline: 'none'
                      }}
                    />
                  ) : (
                    <>
                      <span style={{ flex: 1 }}>{playlist.name}</span>

                      {playlist.playlistContentType && (
                        <span
                          title={playlist.playlistContentType === 'exercise' ? 'Exercise' : 'Interval'}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '19px',
                            height: '19px',
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                            fontSize: '11px',
                            fontWeight: 500,
                            color: '#666',
                            flexShrink: 0
                          }}
                        >
                          {playlist.playlistContentType === 'exercise' ? 'E' : 'I'}
                        </span>
                      )}
                      
                      <button
                        onClick={(e) => startRenaming(e, playlist)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: '#999',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: 0.6,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0.6}
                        title="Rename playlist"
                      >
                        <Pencil size={14} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>

            {playlists.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No playlists yet
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Playlist Editor */}
        <div className="content-area">
          {selectedPlaylist ? (
            <div className="card">
              {/* Header with Tomorrow button */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h2 style={{
  margin: 0,
  fontSize: '20px',
  fontWeight: 600,
  letterSpacing: '-0.3px',
  color: '#1a1a1a'
}}>
                    {selectedPlaylist.name}
                    {viewMode === 'tomorrow' && (
                      <span style={{ color: '#10b981', fontWeight: '500', marginLeft: '8px' }}>
                        — Tomorrow
                      </span>
                    )}
                  </h2>
                  
                  {viewMode === 'current' ? (
                    <button
                      onClick={() => setViewMode('tomorrow')}
                      style={{
  background: '#DCFCE7',
  color: '#16A34A',
  padding: '5px 10px',
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'background-color 0.2s'
}}
onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#BBF7D0'}
onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#DCFCE7'}
                    >
                      <Calendar size={16} />
                      Tomorrow
                    </button>
                  ) : (
                    <button
                      onClick={() => setViewMode('current')}
                      style={{
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      ← Back to Current
                    </button>
                  )}
                </div>
                
                <button
  style={{
    padding: '6px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(230, 57, 70, 0.2)',
    background: 'rgba(230, 57, 70, 0.05)',
    color: '#E63946',
    fontSize: '12px',
    fontWeight: 400,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  }}
  onClick={() => handleDeletePlaylist(selectedPlaylist.id)}
>
  <Trash2 size={14} />
  Delete Playlist
</button>
              </div>

              {/* Schedule info */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', color: '#64748B', fontSize: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={16} />
                  Schedule: {selectedPlaylist.startTime} - {selectedPlaylist.endTime}
                </div>
                {viewMode === 'tomorrow' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981' }}>
                    • Swaps at {formatSwapTime(selectedPlaylist.swapTime)}
                  </div>
                )}
              </div>

              {/* Section title */}
              <h3 style={{
  fontSize: '11px',
  fontWeight: 600,
  color: '#9CA3AF',
  margin: '0 0 12px 0',
  letterSpacing: '1px',
  textTransform: 'uppercase'
}}>
                {viewMode === 'tomorrow' ? "Tomorrow's Assets" : 'Assets in Playlist'}
              </h3>
              
              {/* Asset table with thumbnails */}
              {displayAssets.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th style={{ width: '80px' }}></th>
                      <th>Name</th>
                      <th style={{ width: '120px' }}>Duration</th>
                      <th style={{ width: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayAssets.map((assetId, index) => {
                      const asset = getAssetById(assetId);
                      return asset ? (
                        <tr 
                          key={assetId}
                          style={viewMode === 'tomorrow' ? {
                            backgroundColor: '#f0fdf4'
                          } : {}}
                        >
                          <td style={viewMode === 'tomorrow' ? { color: '#10b981' } : {}}>
                            {index + 1}
                          </td>
                          <td>
                            <PlaylistAssetThumbnail 
                              asset={asset} 
                              onOpen={() => setPreviewAsset(asset)}
                              isTomorrowMode={viewMode === 'tomorrow'}
                            />
                          </td>
                          <td style={{ fontSize: '14px' }}>{asset.name}</td>
<td style={{ fontSize: '14px', color: '#8a8a8a' }}>{formatDuration(asset.durationMs)}</td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px' }}
                              onClick={() => handleRemoveAssetFromPlaylist(assetId)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ) : null;
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px', 
                  color: '#999', 
                  background: viewMode === 'tomorrow' ? '#f0fdf4' : '#f9f9f9', 
                  borderRadius: '4px',
                  border: viewMode === 'tomorrow' ? '1px dashed #10b981' : 'none'
                }}>
                  {viewMode === 'tomorrow' 
                    ? 'No content queued for tomorrow. Add assets from the list below.'
                    : 'No assets in this playlist. Add assets from the list below.'
                  }
                </div>
              )}

              <div style={{ marginTop: '32px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 500 }}>
  Available Assets
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '14px', 
                      fontWeight: 400, 
                      color: '#666' 
                    }}>
                      ({filteredAvailableAssets.filter(a => !displayAssets.includes(a.id)).filter(a => a.name.toLowerCase().includes(assetSearchQuery.toLowerCase())).filter(a => {
                        if (assetScope === 'library') { if (a.userId) return false; }
                        else { if (!a.userId || a.userId !== currentUser?.uid) return false; }
                        return true;
                      }).filter(a => {
                        if (assetTypeFilter === 'all') return true;
                        if (assetTypeFilter === 'interval') return a.type === 'video' && a.source !== 'exercise-builder';
                        if (assetTypeFilter === 'exercise') return a.source === 'exercise-builder' && a.pageMode !== 'program';
                        return true;
                      }).length})
                    </span>
                  </h3>
                  <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px', flexShrink: 0 }}>
                    {[
                      { id: 'library', label: 'Library' },
                      { id: 'myfiles', label: 'My files' },
                    ].filter(tab => !(tab.id === 'library' && selectedPlaylist?.playlistContentType === 'exercise')).map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setAssetScope(tab.id)}
                        style={{
                          padding: '5px 12px',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          background: assetScope === tab.id ? 'white' : 'transparent',
                          color: assetScope === tab.id ? '#E63946' : '#666',
                          boxShadow: assetScope === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ position: 'relative' }}>
                  <Search 
                    size={16} 
                    style={{ 
                      position: 'absolute', 
                      left: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#999'
                    }} 
                  />
                  <input
                    type="text"
                    placeholder="Search assets..."
                    value={assetSearchQuery}
                    onChange={(e) => setAssetSearchQuery(e.target.value)}
                    style={{
                      padding: '8px 10px 8px 34px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      width: '200px',
                      outline: 'none'
                    }}
                  />
                  {assetSearchQuery && (
                    <button
                      onClick={() => setAssetSearchQuery('')}
                      style={{
                        position: 'absolute',
                        right: '6px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#999',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                </div>
              </div>

              {assetSearchQuery && (
                <div style={{ marginBottom: '12px', color: '#666', fontSize: '14px' }}>
                  Search results for "<strong>{assetSearchQuery}</strong>"
                </div>
              )}

              {/* Available assets grid - filtered by scope, type, and equipment profile */}
              <div className="assets-grid">
                {filteredAvailableAssets
                  .filter(a => !displayAssets.includes(a.id))
                  .filter(a => a.name.toLowerCase().includes(assetSearchQuery.toLowerCase()))
                  .filter(a => {
                    if (assetScope === 'library') return !a.userId;
                    return a.userId === currentUser?.uid;
                  })
                  .filter(a => {
                    if (assetTypeFilter === 'all') return true;
                    if (assetTypeFilter === 'interval') return a.type === 'video' && a.source !== 'exercise-builder';
                    if (assetTypeFilter === 'exercise') return a.source === 'exercise-builder' && a.pageMode !== 'program';
                    return true;
                  })
                  .map(asset => (
                  <AvailableAssetCard 
                    key={asset.id} 
                    asset={asset} 
                    onAdd={() => handleAddAssetToPlaylist(asset.id)}
                    formatDuration={formatDuration}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              Select a playlist to edit or create a new one
            </div>
          )}
        </div>
      </div>

      {/* MOBILE LAYOUT */}
      <div className="mobile-only">
        {/* Mobile List View */}
        <div className={`mobile-slide-view ${mobileView === 'list' ? 'active' : 'slide-out-left'}`}>
          <div className="mobile-list">
            {playlists.map((playlist, index) => (
              <div 
                key={playlist.id} 
                className="mobile-list-item"
                onClick={() => handleSelectPlaylist(playlist)}
              >
                <div 
                  className="playlist-dot" 
                  style={{ width: '12px', height: '12px', flexShrink: 0, ...(hasPendingContent(playlist) ? { backgroundColor: '#10b981' } : {}) }}
                  title={hasPendingContent(playlist) ? 'Has tomorrow content' : undefined}
                ></div>
                
                <div className="mobile-list-content">
                  <div className="mobile-list-title">{playlist.name}</div>
                  <div className="mobile-list-subtitle">
                    {playlist.assets?.length || 0} assets • {getTotalDuration(playlist)}
                  </div>
                </div>

                {playlist.playlistContentType && (
                  <span
                    title={playlist.playlistContentType === 'exercise' ? 'Exercise' : 'Interval'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '1px solid #ccc',
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#666',
                      flexShrink: 0
                    }}
                  >
                    {playlist.playlistContentType === 'exercise' ? 'E' : 'I'}
                  </span>
                )}
                
                <div className="mobile-list-menu" ref={openMenuId === playlist.id ? menuRef : null}>
                  <button 
                    className="more-btn"
                    onClick={(e) => toggleMenu(playlist.id, e)}
                    aria-label="More options"
                  >
                    <MoreVertical size={20} />
                  </button>
                  
                  {openMenuId === playlist.id && (
                    <div className={`dropdown-menu ${index === 0 ? 'dropdown-down' : ''}`}>
                      <button 
                        className="dropdown-item"
                        onClick={(e) => { e.stopPropagation(); startRenaming(e, playlist); }}
                      >
                        Rename
                      </button>
                      <button 
                        className="dropdown-item dropdown-item-danger"
                        onClick={(e) => { e.stopPropagation(); handleDeletePlaylist(playlist.id); }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {playlists.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No playlists yet. Tap + to create one.
              </div>
            )}
          </div>
        </div>

        {/* Mobile Detail View */}
        <div className={`mobile-slide-view ${mobileView === 'detail' ? 'active' : 'slide-out-right'}`}>
          {selectedPlaylist && (
            <>
              {/* Mobile Detail Header */}
              <div className="mobile-detail-header">
                <button className="back-btn" onClick={handleBackToList}>
                  <ChevronLeft size={24} />
                </button>
                <h2 className="mobile-detail-title">
                  {selectedPlaylist.name}
                  {viewMode === 'tomorrow' && (
                    <span style={{ color: '#10b981', fontSize: '14px', marginLeft: '8px' }}>
                      — Tomorrow
                    </span>
                  )}
                </h2>
                <div style={{ width: '40px' }}></div>
              </div>

              {/* Tomorrow toggle button for mobile */}
              <div style={{ padding: '0 16px', marginBottom: '8px' }}>
                {viewMode === 'current' ? (
                  <button
                    onClick={() => setViewMode('tomorrow')}
                   style={{
  width: '100%',
  background: '#DCFCE7',
  color: '#16A34A',
  border: 'none',
  borderRadius: '6px',
  padding: '12px 16px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
}}
                  >
                    <Calendar size={18} />
                    View Tomorrow's Content
                  </button>
                ) : (
                  <button
                    onClick={() => setViewMode('current')}
                    style={{
                      width: '100%',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    ← Back to Current Content
                  </button>
                )}
              </div>

              {/* Playlist Info Bar */}
              <div className="mobile-detail-info">
                <span>Total time: <strong>{getTotalDuration(selectedPlaylist)}</strong></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Playlist color 
                  <span className="playlist-dot" style={{ width: '12px', height: '12px' }}></span>
                </span>
              </div>

              {/* Swap time info for tomorrow mode */}
              {viewMode === 'tomorrow' && (
                <div style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0fdf4',
                  color: '#059669',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Clock size={14} />
                  Swaps at {formatSwapTime(selectedPlaylist.swapTime)}
                </div>
              )}

              {/* Assets Section */}
              <div className="mobile-detail-section">
                <div className="mobile-detail-section-header">
                  <span>{viewMode === 'tomorrow' ? "Tomorrow's Assets" : 'Assets'}</span>
                </div>

                {displayAssets.length > 0 ? (
                  <div className="mobile-asset-list">
                    {displayAssets.map((assetId, index) => {
                      const asset = getAssetById(assetId);
                      return asset ? (
                        <div 
                          key={assetId} 
  className="mobile-asset-item"
  style={viewMode === 'tomorrow' ? {
    backgroundColor: '#f0fdf4',
    cursor: asset.type === 'video' ? 'pointer' : 'default'
  } : {
    cursor: asset.type === 'video' ? 'pointer' : 'default'
  }}
  onClick={(e) => {
    if (asset.type === 'video') {
      e.preventDefault();
      setPreviewAsset(asset);
    }
  }}
                        >
                          {/* Thumbnail */}
                          <div 
                            style={{
                              width: '48px',
                              height: '32px',
                              borderRadius: '0px',
                              overflow: 'hidden',
                              flexShrink: 0,
                              background: '#1a1a1a',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {asset.spriteUrl ? (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundImage: `url(${asset.spriteUrl})`,
                                  backgroundPosition: '0% 0',
                                  backgroundSize: '400% 100%',
                                  backgroundRepeat: 'no-repeat'
                                }}
                              />
                            ) : asset.thumbnailUrl ? (
                              <img 
                                src={asset.thumbnailUrl} 
                                alt={asset.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Film size={20} color="#666" />
                            )}
                          </div>
                          
                          <div className="mobile-asset-info" style={{ flex: 1 }}>
                            <div className="mobile-asset-name">{asset.name}</div>
                          </div>
                          <div className="mobile-asset-duration">
                            {formatDuration(asset.durationMs)}
                            <button
                              className="mobile-asset-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAssetFromPlaylist(assetId);
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <div style={{ 
                    padding: '24px', 
                    textAlign: 'center', 
                    color: viewMode === 'tomorrow' ? '#059669' : '#999',
                    backgroundColor: viewMode === 'tomorrow' ? '#f0fdf4' : 'transparent'
                  }}>
                    {viewMode === 'tomorrow' 
                      ? 'No content queued for tomorrow. Tap + to add assets.'
                      : 'No assets yet. Tap + to add assets.'
                    }
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Floating Action Button (Mobile) */}
      <button 
        className="fab" 
        onClick={() => mobileView === 'list' ? setShowAddModal(true) : setShowAssetPicker(true)} 
        aria-label={mobileView === 'list' ? 'Create playlist' : 'Add asset'}
        style={mobileView === 'detail' && viewMode === 'tomorrow' ? {
          backgroundColor: '#10b981'
        } : {}}
      >
        <Plus size={24} />
      </button>

      {/* Add Playlist Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setNewPlaylistContentType(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Playlist</h2>
              <button className="modal-close" onClick={() => { setShowAddModal(false); setNewPlaylistContentType(''); }}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">Playlist Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Station 1"
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddPlaylist()}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">Content Type *</label>
              <select
                className="form-input"
                value={newPlaylistContentType}
                onChange={(e) => setNewPlaylistContentType(e.target.value)}
              >
                <option value="">Select content type…</option>
                <option value="interval">Interval</option>
                <option value="exercise">Exercise</option>
              </select>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                A playlist holds one content type. This can't be changed later.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowAddModal(false); setNewPlaylistContentType(''); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddPlaylist}>
                Create Playlist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Asset Picker - filtered by equipment profile */}
      {showAssetPicker && (
        <div className="mobile-asset-picker">
          <div className="asset-picker-header">
            <button className="back-btn" onClick={() => setShowAssetPicker(false)}>
              <ChevronLeft size={24} />
            </button>
            <span className="asset-picker-title">
              {viewMode === 'tomorrow' ? 'Select Asset for Tomorrow' : 'Select Asset'}
            </span>
            <button className="asset-picker-done" onClick={() => setShowAssetPicker(false)}>
              Done
            </button>
          </div>
          
          <div className="asset-picker-search">
            <input
              type="text"
              placeholder="Search assets..."
              value={assetSearchQuery}
              onChange={(e) => setAssetSearchQuery(e.target.value)}
            />
          </div>

          <div className="asset-picker-scroll">
            <div className="assets-grid">
              {filteredAvailableAssets
                .filter(a => !displayAssets.includes(a.id))
                .filter(a => a.name.toLowerCase().includes(assetSearchQuery.toLowerCase()))
                .map(asset => (
                  <div 
                    key={asset.id} 
                    className="asset-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleAddAssetToPlaylist(asset.id)}
                  >
                    <div 
                      className="asset-thumbnail"
                      style={{ position: 'relative', overflow: 'hidden' }}
                    >
                      {asset.spriteUrl ? (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${asset.spriteUrl})`,
                            backgroundPosition: '0% 0',
                            backgroundSize: '400% 100%',
                            backgroundRepeat: 'no-repeat'
                          }}
                        />
                      ) : asset.thumbnailUrl ? (
                        <img 
                          src={asset.thumbnailUrl} 
                          alt={asset.name}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%',
                          color: '#666',
                          background: '#1a1a1a'
                        }}>
                          <Film size={48} />
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        background: 'rgba(0,0,0,0.75)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        {formatDuration(asset.durationMs)}
                      </div>
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        background: 'rgba(0,0,0,0.5)',
                        border: '2px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        <Plus size={14} />
                      </div>
                    </div>
                    <div className="asset-info">
                      <div className="asset-name" style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a2e' }}>{asset.name}</div>
                      <div className="asset-meta">
                        {formatDuration(asset.durationMs)}
                      </div>
                    </div>
                  </div>
                ))}
            
              {filteredAvailableAssets.filter(a => !displayAssets.includes(a.id)).length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '24px', textAlign: 'center', color: '#999' }}>
                  No available assets to add.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewAsset && (
        <VideoPreviewModal 
          asset={previewAsset} 
          onClose={() => setPreviewAsset(null)} 
        />
      )}
    </div>
  );
}

// Thumbnail component for playlist assets with hover popup (Desktop)
function PlaylistAssetThumbnail({ asset, onOpen, isTomorrowMode }) {
  const [isHovering, setIsHovering] = useState(false);
  const [spriteFrame, setSpriteFrame] = useState(0);
  const scrubIntervalRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const startScrubbing = () => {
    if (asset.type !== 'video' || !asset.spriteUrl) return;
    
    setSpriteFrame(0);
    if (scrubIntervalRef.current) clearInterval(scrubIntervalRef.current);
    
    scrubIntervalRef.current = setInterval(() => {
      setSpriteFrame(prev => (prev + 1) % 4);
    }, 600);
  };

  const stopScrubbing = () => {
    setSpriteFrame(0);
    if (scrubIntervalRef.current) {
      clearInterval(scrubIntervalRef.current);
      scrubIntervalRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovering(true);
    startScrubbing();
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      stopScrubbing();
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (scrubIntervalRef.current) clearInterval(scrubIntervalRef.current);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  return (
    <div 
      style={{ position: 'relative' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: '64px',
          height: '36px',
          borderRadius: '0px',
          overflow: 'hidden',
          background: '#1a1a1a',
          cursor: 'pointer',
          border: isTomorrowMode ? '2px solid #10b981' : 'none'
        }}
        onClick={onOpen}
      >
        {asset.type === 'video' ? (
          <>
            {asset.spriteUrl ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${asset.spriteUrl})`,
                  backgroundPosition: `${(spriteFrame / 3) * 100}% 0`,
                  backgroundSize: '400% 100%',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : asset.thumbnailUrl ? (
              <img 
                src={asset.thumbnailUrl} 
                alt={asset.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: '#666'
              }}>
                <Film size={20} />
              </div>
            )}
          </>
        ) : (
          <img 
            src={asset.url} 
            alt={asset.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>

      {/* Hover popup */}
      {isHovering && asset.type === 'video' && (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            left: '74px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            padding: '12px',
            zIndex: 100,
            width: '200px'
          }}
        >
          {/* Preview thumbnail */}
          <div
            style={{
              width: '100%',
              height: '100px',
              borderRadius: '0px',
              overflow: 'hidden',
              marginBottom: '8px',
              background: '#1a1a1a'
            }}
          >
            {asset.spriteUrl ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${asset.spriteUrl})`,
                  backgroundPosition: `${(spriteFrame / 3) * 100}% 0`,
                  backgroundSize: '400% 100%',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : asset.thumbnailUrl ? (
              <img 
                src={asset.thumbnailUrl} 
                alt={asset.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: '#666'
              }}>
                <Film size={32} />
              </div>
            )}
          </div>

          {/* Filename */}
          <div
            style={{
              fontSize: '13px',
              color: '#333',
              marginBottom: '8px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {asset.name}
          </div>

          {/* Open button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            style={{
              width: '100%',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <Play size={16} />
            Open
          </button>
        </div>
      )}
    </div>
  );
}

// Asset card component for available assets grid (Desktop)
function AvailableAssetCard({ asset, onAdd, formatDuration }) {
  const [isHovering, setIsHovering] = useState(false);
  const [spriteFrame, setSpriteFrame] = useState(0);
  const scrubIntervalRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovering(true);
    
    if (asset.type !== 'video' || !asset.spriteUrl) return;
    
    setSpriteFrame(0);
    scrubIntervalRef.current = setInterval(() => {
      setSpriteFrame(prev => (prev + 1) % 4);
    }, 600);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setSpriteFrame(0);
    
    if (scrubIntervalRef.current) {
      clearInterval(scrubIntervalRef.current);
      scrubIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (scrubIntervalRef.current) {
        clearInterval(scrubIntervalRef.current);
      }
    };
  }, []);

  return (
    <div 
  className="asset-card" 
  style={{ 
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    transform: isHovering ? 'translateY(-4px)' : 'translateY(0)',
    boxShadow: isHovering ? '0 12px 40px rgba(0,0,0,0.12)' : ''
  }} 
  onClick={onAdd}
>
      <div 
        className="asset-thumbnail"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        {asset.spriteUrl && isHovering ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${asset.spriteUrl})`,
              backgroundPosition: `${(spriteFrame / 3) * 100}% 0`,
              backgroundSize: '400% 100%',
              backgroundRepeat: 'no-repeat'
            }}
          />
        ) : (
          <>
            {asset.spriteUrl ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${asset.spriteUrl})`,
                  backgroundPosition: '0% 0',
                  backgroundSize: '400% 100%',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : asset.thumbnailUrl ? (
              <img 
                src={asset.thumbnailUrl} 
                alt={asset.name}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: '#666',
                background: '#1a1a1a'
              }}>
                <Film size={32} />
              </div>
            )}
          </>
        )}

        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          background: isHovering ? 'linear-gradient(135deg, #E63946 0%, #D62839 100%)' : 'rgba(255,255,255,0.9)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isHovering ? '#fff' : '#E63946',
          transition: 'all 0.2s ease'
        }}>
          <Plus size={14} />
        </div>

        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          background: 'rgba(0,0,0,0.75)',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 500
        }}>
          {formatDuration(asset.durationMs)}
        </div>
      </div>
      <div className="asset-info">
        <div className="asset-name" style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a2e' }}>{asset.name}</div>
        <div className="asset-meta">{formatDuration(asset.durationMs)}</div>
      </div>
    </div>
  );
}

export default Playlists;